/**
 * Canonical URL policy and site-level identity.
 *
 * THE CANONICAL RULE, decided once here so nothing has to guess:
 *
 *   https  +  apex host (no www)  +  lowercase-as-routed  +  TRAILING SLASH
 *
 * WHY THE TRAILING SLASH -- this is load-bearing, do not "tidy" it away.
 *
 * Pre-rendering writes one file per route: build/about/index.html. On a plain
 * static host that directory is served at /about/. Apache's mod_dir then
 * answers a request for /about with a 301 to /about/ -- that is DirectorySlash,
 * which is On by default.
 *
 * We previously canonicalised to the no-slash form and switched DirectorySlash
 * Off in .htaccess to make it work. That was verified against a local Apache
 * and was correct there. In production it failed: the host did not apply the
 * new .htaccess at all, so DirectorySlash stayed On and every single canonical
 * URL 301'd. Auditors fetching the canonical received a 301 stub instead of the
 * page, concluded the content was not in the raw HTML, and reported a ~2300%
 * "rendering percentage".
 *
 * Canonicalising WITH the slash means the canonical URL is the exact URL the
 * filesystem already serves, with no rewrite rule involved. It is correct
 * whether or not .htaccess is honoured, on Apache, Nginx, Netlify or any CDN.
 * The site root is unchanged: "https://nayakstutorials.com/".
 */

const businessConfig = require("./businessConfig");

const ORIGIN = "https://nayakstutorials.com";

/**
 * Normalise any route into the one canonical absolute URL for that page.
 * Accepts "/about", "about", "/about/" and "https://nayakstutorials.com/about"
 * and returns "https://nayakstutorials.com/about/" for all four.
 */
function canonicalFor(routePath) {
  if (!routePath) return `${ORIGIN}/`;

  let path = String(routePath).trim();

  // Tolerate a full URL being passed in.
  path = path.replace(/^https?:\/\/[^/]+/i, "");

  // Drop query strings and fragments -- neither belongs in a canonical.
  path = path.split("?")[0].split("#")[0];

  if (!path.startsWith("/")) path = `/${path}`;

  // Collapse duplicate slashes, then guarantee exactly one trailing slash so
  // the canonical matches the directory the static host actually serves.
  path = path.replace(/\/{2,}/g, "/").replace(/\/+$/, "");

  return path === "" ? `${ORIGIN}/` : `${ORIGIN}${path}/`;
}

/**
 * The in-app path for a route, for use in href/to attributes.
 * Mirrors canonicalFor() so internal links point at the canonical URL and do
 * not cost a redirect hop when a crawler follows them.
 */
function pathFor(routePath) {
  return canonicalFor(routePath).replace(ORIGIN, "");
}

const siteConfig = {
  origin: ORIGIN,
  name: businessConfig.legalName,
  locale: "en_IN",
  language: "en-IN",

  /**
   * Default share image. Absolute URL, served from /public so it is crawlable
   * without a build hash. Programme pages override this where a genuinely
   * relevant image exists -- reusing one generic image everywhere is explicitly
   * what we are avoiding.
   */
  defaultOgImage: `${ORIGIN}/nayaks-logo.png`,
  defaultOgImageAlt: `${businessConfig.legalName} logo`,
  defaultOgImageWidth: 600,
  defaultOgImageHeight: 111,

  twitterCard: "summary_large_image",

  /**
   * Robots directive for ordinary indexable pages.
   *
   * max-snippet:-1 and max-image-preview:large matter for AI surfaces: they
   * remove the length cap on the text snippet an engine may quote and permit a
   * large image. Restricting them limits how much of the page an answer engine
   * is allowed to reproduce when citing it.
   */
  defaultRobots:
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",

  /** For pages that must exist but must never rank. */
  noindexRobots: "noindex, follow",

  /** Stable JSON-LD node identifiers. */
  ids: {
    organization: `${ORIGIN}/#organization`,
    website: `${ORIGIN}/#website`,
    logo: `${ORIGIN}/#logo`,
    primaryLocation: `${ORIGIN}/#primary-location`,
  },

  canonicalFor,
  pathFor,
};

module.exports = siteConfig;
